import { createFileRoute, Link } from '@tanstack/react-router';
import type { LucideIcon } from 'lucide-react';
import { BanknoteIcon, ClipboardCheckIcon, HandshakeIcon, MapIcon, UserXIcon } from 'lucide-react';
import { BarChart, type ChartPoint } from '@/components/mini-chart';
import { ListSkeleton } from '@/components/states';
import { Card, CardBody, SectionTitle } from '@/components/ui/card';
import { useAuth } from '@/lib/auth/context';
import { integer, relativeId, rupiahCompact } from '@/lib/format';
import { db, META_KEYS } from '@/offline/db';
import { dailyStats, statsSeries } from '@/offline/queries';
import { useOfflineQuery } from '@/offline/use-offline-query';

export const Route = createFileRoute('/_app/')({
  component: HomePage,
});

const DAY_LABELS = ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'];

/**
 * Beranda collector (T-P7-09/10, FR-DASH-09).
 *
 * Seluruh angka dihitung dari data yang ada di perangkat, dan setiap kartu
 * membawa label "Data per …". Angka tanpa keterangan usia adalah jebakan di
 * aplikasi offline: yang terlihat sebagai target belum tercapai bisa saja
 * pencapaian yang sudah dilaporkan tetapi belum tersinkron.
 */
function HomePage() {
  const { user } = useAuth();

  const { data: today, loading } = useOfflineQuery(() => dailyStats(), []);
  const { data: series } = useOfflineQuery(() => statsSeries(7), []);
  const { data: lastSyncAt } = useOfflineQuery(
    async () => (await db.meta.get(META_KEYS.lastSyncAt))?.value as string | undefined,
    [],
  );

  const chartPoints: ChartPoint[] = (series ?? []).map((entry) => {
    const date = new Date(`${entry.date}T00:00:00`);
    return {
      label: DAY_LABELS[date.getDay()] ?? '',
      value: entry.visits,
      description: `${date.toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long' })}: ${entry.visits} kunjungan, ${entry.paid} berhasil ditagih`,
    };
  });

  return (
    <div className="space-y-5 px-4 py-4">
      <header className="space-y-0.5">
        <p className="text-sm text-muted-foreground">Selamat bertugas,</p>
        <h1 className="text-lg font-semibold tracking-tight">{user?.fullName}</h1>
        <p className="text-xs text-muted-foreground">Data per {relativeId(lastSyncAt)}</p>
      </header>

      {loading || !today ? (
        <ListSkeleton rows={2} />
      ) : (
        <>
          <section className="grid grid-cols-2 gap-3">
            <StatCard
              icon={ClipboardCheckIcon}
              label="Kunjungan hari ini"
              value={integer(today.visits)}
            />
            <StatCard
              icon={BanknoteIcon}
              label="Tertagih hari ini"
              value={rupiahCompact(today.paidAmount)}
              hint={`${today.paid} pembayaran`}
            />
            <StatCard
              icon={HandshakeIcon}
              label="Janji bayar"
              value={integer(today.ptp)}
              hint={rupiahCompact(today.ptpAmount)}
            />
            <StatCard
              icon={UserXIcon}
              label="Tidak berhasil"
              value={integer(today.failed)}
              hint={
                today.pendingSync > 0 ? `${today.pendingSync} menunggu kirim` : 'Semua terkirim'
              }
            />
          </section>

          <section className="space-y-2">
            <SectionTitle>Pencapaian 7 hari</SectionTitle>
            <Card>
              <CardBody>
                <BarChart points={chartPoints} />
              </CardBody>
            </Card>
          </section>
        </>
      )}

      <section className="space-y-2">
        <SectionTitle>Mulai kerja</SectionTitle>
        <div className="grid grid-cols-2 gap-3">
          <QuickLink to="/nasabah" icon={ClipboardCheckIcon} label="Daftar nasabah" />
          <QuickLink to="/peta" icon={MapIcon} label="Peta sebaran" />
        </div>
      </section>
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <Card>
      <CardBody className="space-y-1 p-3">
        <Icon className="size-4 text-muted-foreground" aria-hidden="true" />
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-lg font-semibold tabular-nums">{value}</p>
        {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      </CardBody>
    </Card>
  );
}

function QuickLink({ to, icon: Icon, label }: { to: string; icon: LucideIcon; label: string }) {
  return (
    <Link
      to={to}
      className="flex min-h-touch items-center gap-2 rounded-card border border-border bg-surface px-3 py-3 text-sm font-medium active:bg-muted"
    >
      <Icon className="size-4 text-brand" aria-hidden="true" />
      {label}
    </Link>
  );
}
